import * as RC from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { cn } from './cn';

/*
  Checkbox — выбор в составе формы или списка (сохраняется кнопкой), в отличие от Switch.
  Поддерживает состояние indeterminate (частичный выбор в таблицах).
*/

interface CheckboxProps extends ComponentPropsWithoutRef<typeof RC.Root> {
  label?: ReactNode;
  description?: ReactNode;
}

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ label, description, className, id, checked, ...rest }, ref) => {
    const reactId = useId();
    const boxId = id ?? `checkbox-${reactId}`;
    const control = (
      <RC.Root
        ref={ref}
        id={boxId}
        checked={checked}
        data-slot="checkbox"
        className={cn(
          'peer grid size-4 shrink-0 place-items-center rounded-xs border border-line-strong bg-card',
          'transition-colors duration-fast',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'data-[state=checked]:border-brand-solid data-[state=checked]:bg-brand-solid data-[state=checked]:text-on-brand',
          'data-[state=indeterminate]:border-brand-solid data-[state=indeterminate]:bg-brand-solid data-[state=indeterminate]:text-on-brand',
          className,
        )}
        {...rest}
      >
        <RC.Indicator className="grid place-items-center">
          {checked === 'indeterminate' ? (
            <Minus size={12} strokeWidth={3} />
          ) : (
            <Check size={12} strokeWidth={3} />
          )}
        </RC.Indicator>
      </RC.Root>
    );
    if (!label) return control;
    return (
      <div className="flex items-start gap-2">
        <span className="pt-0.5">{control}</span>
        <label htmlFor={boxId} className="flex cursor-pointer flex-col gap-0.5">
          <span className="text-sm text-fg">{label}</span>
          {description && <span className="text-xs text-fg-muted">{description}</span>}
        </label>
      </div>
    );
  },
);
Checkbox.displayName = 'Checkbox';
